/**
 * The ticket pickers' vocabulary (categories and products), managed by admins in Settings. Pure
 * rules: the API runs them inside its transaction, the desk runs the same checks before sending.
 *
 * A value is hidden, never deleted: tickets keep the text they were given, hiding only takes it
 * out of the pickers. "Other" is the one catch-all for each kind and can be neither hidden nor
 * renamed. Names are unique within a kind ignoring case and surrounding or doubled spaces.
 */
export const VOCAB_KINDS = ['category', 'product'];
export const VOCAB_KIND_WORD = { category: 'category', product: 'product' };
export const VOCAB_NAME_MAX = 60;
export const OTHER_VOCAB = 'Other';
export const VOCAB_REFUSAL = {
    not_admin: 'Only an admin can change these lists.',
    empty_name: 'Give it a name.',
    long_name: `Keep the name to ${String(VOCAB_NAME_MAX)} characters.`,
    duplicate: 'That name is already on the list.',
    not_found: 'That value is no longer on the list.',
    protected: '"Other" stays on every list and keeps its name.',
    bad_order: 'The order does not match the list. Reload and try again.',
};
/** Trim and collapse inner whitespace; what is stored and what is compared. */
export const normalizeVocabName = (name) => name.replace(/\s+/g, ' ').trim();
const sameName = (a, b) => normalizeVocabName(a).toLowerCase() === normalizeVocabName(b).toLowerCase();
/** The catch-all: present on every list, never hidden or renamed. */
export const isProtectedVocab = (name) => sameName(name, OTHER_VOCAB);
function checkName(name) {
    const n = normalizeVocabName(name);
    if (n === '')
        return { ok: false, reason: 'empty_name' };
    if (n.length > VOCAB_NAME_MAX)
        return { ok: false, reason: 'long_name' };
    return { ok: true, name: n };
}
/** The items of one kind in picker order (sort, then name as a tiebreak so the order is total). */
export function vocabOfKind(items, kind) {
    return items.filter((i) => i.kind === kind).sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
}
/** What the pickers show: active values of a kind, in order. */
export function activeVocabNames(items, kind) {
    return vocabOfKind(items, kind)
        .filter((i) => i.active)
        .map((i) => i.name);
}
/** A new value goes to the end of its kind (after "Other", which an admin can then move). */
export function planVocabAdd(items, kind, name, actorIsAdmin) {
    if (!actorIsAdmin)
        return { ok: false, reason: 'not_admin' };
    const c = checkName(name);
    if (!c.ok)
        return c;
    const ofKind = vocabOfKind(items, kind);
    if (ofKind.some((i) => sameName(i.name, c.name)))
        return { ok: false, reason: 'duplicate' };
    const last = ofKind.at(-1);
    return { ok: true, name: c.name, sort: (last?.sort ?? 0) + 10 };
}
/** A case-only change of the same name is a rename too (it is not a duplicate of itself). */
export function planVocabRename(items, id, name, actorIsAdmin) {
    if (!actorIsAdmin)
        return { ok: false, reason: 'not_admin' };
    const item = items.find((i) => i.id === id);
    if (!item)
        return { ok: false, reason: 'not_found' };
    if (isProtectedVocab(item.name))
        return { ok: false, reason: 'protected' };
    const c = checkName(name);
    if (!c.ok)
        return c;
    if (isProtectedVocab(c.name))
        return { ok: false, reason: 'duplicate' };
    if (items.some((i) => i.kind === item.kind && i.id !== id && sameName(i.name, c.name)))
        return { ok: false, reason: 'duplicate' };
    return { ok: true, item, name: c.name };
}
export function planVocabSetActive(items, id, active, actorIsAdmin) {
    if (!actorIsAdmin)
        return { ok: false, reason: 'not_admin' };
    const item = items.find((i) => i.id === id);
    if (!item)
        return { ok: false, reason: 'not_found' };
    if (!active && isProtectedVocab(item.name))
        return { ok: false, reason: 'protected' };
    return { ok: true, item, changed: item.active !== active };
}
/**
 * `ids` is the whole kind in its new order, hidden values included, so a stale screen cannot drop
 * a value it did not know about. Returns the sort each id gets (10, 20, ...).
 */
export function planVocabReorder(items, kind, ids, actorIsAdmin) {
    if (!actorIsAdmin)
        return { ok: false, reason: 'not_admin' };
    const ofKind = vocabOfKind(items, kind);
    const known = new Set(ofKind.map((i) => i.id));
    if (ids.length !== ofKind.length || new Set(ids).size !== ids.length || ids.some((id) => !known.has(id)))
        return { ok: false, reason: 'bad_order' };
    return { ok: true, sorts: ids.map((id, i) => ({ id, sort: (i + 1) * 10 })) };
}
/** The ids of a kind with one item moved a step up or down; the same list when it cannot move. */
export function movedOrder(items, kind, id, direction) {
    const ids = vocabOfKind(items, kind).map((i) => i.id);
    const at = ids.indexOf(id);
    const to = direction === 'up' ? at - 1 : at + 1;
    if (at < 0 || to < 0 || to >= ids.length)
        return ids;
    const next = [...ids];
    const a = next[at];
    const b = next[to];
    if (a === undefined || b === undefined)
        return ids;
    next[at] = b;
    next[to] = a;
    return next;
}
/** A rule-based suggestion only stands while that category is on the picker; a hidden one is no suggestion. */
export function allowedSuggestion(suggested, activeCategories) {
    if (suggested === null)
        return null;
    return activeCategories.some((c) => sameName(c, suggested)) ? suggested : null;
}
//# sourceMappingURL=vocab.js.map